import { useEffect, useRef, useMemo, useState } from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import { getSchools } from "services/api/schoolApi";
import { getTodayAttendance } from "services/api/attendanceApi";
import { getJourneyPlans } from "services/api/journeyPlanApi";
import {
  createVisit,
  updateVisit,
  getNextVisitNumber,
  getVisitHistory,
} from "services/api/visitApi";

import { showSuccess, showError } from "utils/toast";

const getToday = () => new Date().toISOString().substring(0, 10);

const INITIAL_FORM = {
  // Backend should generate this from historical visits.
  // It is intentionally read-only in the UI.
  visitNumber: "",

  visitDate: getToday(),
  visitType: "school",

  // School = dropdown
  schoolId: "",

  // Party = manual text field
  partyName: "",

  // Kept for backward compatibility with older payloads.
  partyId: "",

  journeyPlanItemId: "",
  attendanceId: "",

  personMet: "",
  designation: "",
  contactNumber: "",
  purpose: "",
  assessment: "",
  followUp: "",
  remarks: "",

  latitude: "",
  longitude: "",
  address: "",

  peopleMet: [],
  photos: [],
  followups: [],
  samples: [],
  orders: [],
  payments: [],
};

const extractSchools = (response) =>
  response?.data?.schools ||
  response?.schools ||
  response?.data?.data?.schools ||
  [];

const extractAttendance = (response) =>
  response?.data?.attendance ||
  response?.attendance ||
  response?.data?.data?.attendance ||
  null;

const extractJourneyPlans = (response) =>
  response?.data?.journeyPlans ||
  response?.journeyPlans ||
  response?.data?.data?.journeyPlans ||
  response?.data?.data ||
  [];

function SearchableSchoolSelect({ label, value, schools, loading, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  const selectedSchool = useMemo(
    () => schools.find((school) => String(school.id) === String(value)),
    [schools, value]
  );

  const filteredSchools = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return schools;

    return schools.filter((school) => {
      const searchableText = [
        school.name,
        school.schoolName,
        school.schoolCode,
        school.city?.name,
        school.cityName,
        school.district?.name,
        school.districtName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(keyword);
    });
  }, [schools, search]);

  useEffect(() => {
    const handleOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsidePointer);
    return () => document.removeEventListener("mousedown", handleOutsidePointer);
  }, []);

  const selectSchool = (school) => {
    onChange(String(school.id));
    setSearch("");
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      setSearch("");
      return;
    }

    if (event.key === "Enter" && isOpen) {
      event.preventDefault();
      if (filteredSchools.length > 0) selectSchool(filteredSchools[0]);
    }
  };

  const selectedLabel = selectedSchool
    ? selectedSchool.name ||
      selectedSchool.schoolName ||
      `School #${selectedSchool.id}`
    : "";

  return (
    <div className="relative min-w-0">
      <label className="mb-1 block text-sm font-medium text-gray-800">
        {label}
      </label>

      <div ref={rootRef} className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="visit-school-options"
          autoComplete="off"
          value={isOpen ? search : selectedLabel}
          onFocus={() => {
            setSearch("");
            setIsOpen(true);
          }}
          onChange={(event) => {
            setSearch(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={loading}
          placeholder={loading ? "Loading schools..." : "Search school by name or code..."}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100"
        />

        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>

        {isOpen && !loading && (
          <div
            id="visit-school-options"
            role="listbox"
            className="absolute left-0 right-0 z-[100] mt-1 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl"
          >
            {filteredSchools.length > 0 ? (
              filteredSchools.map((school) => {
                const schoolName =
                  school.name || school.schoolName || `School #${school.id}`;
                const meta = [
                  school.schoolCode,
                  school.city?.name || school.cityName,
                  school.district?.name || school.districtName,
                ]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <button
                    key={String(school.id)}
                    type="button"
                    role="option"
                    aria-selected={String(school.id) === String(value)}
                    onClick={() => selectSchool(school)}
                    className={`block w-full px-4 py-2.5 text-left text-sm transition hover:bg-indigo-50 ${
                      String(school.id) === String(value)
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-gray-800"
                    }`}
                  >
                    <span className="block font-medium">{schoolName}</span>
                    {meta && <span className="mt-0.5 block text-xs text-gray-500">{meta}</span>}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500">
                No schools match “{search}”. Try another name or school code.
              </div>
            )}
          </div>
        )}
      </div>

      <p className="mt-1 text-xs text-gray-500">
        Type a few letters to quickly find the school.
      </p>
    </div>
  );
}

export default function VisitModal({open, visit = null, onClose, reload}) {
  const isEditMode = Boolean(visit?.id);
  const [loading, setLoading] = useState(false);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const [schools, setSchools] = useState([]);
  const [journeyPlans, setJourneyPlans] = useState([]);
  const [visitHistory, setVisitHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [form, setForm] = useState(INITIAL_FORM);

  useEffect(() => {
    if (!open) return;

    const schoolId = form.schoolId;
    const partyName = form.partyName.trim();

    if (form.visitType === "school" && !schoolId) {
      setVisitHistory([]);
      return;
    }

    if (form.visitType === "party" && partyName.length < 2) {
      setVisitHistory([]);
      return;
    }

    let cancelled = false;
    setHistoryLoading(true);

    const timer = setTimeout(async () => {
      try {
        const response = await getVisitHistory({
          schoolId: form.visitType === "school" ? schoolId : "",
          partyName: form.visitType === "party" ? partyName : "",
        });

        if (cancelled) return;

        const history =
          response?.data?.history ||
          response?.history ||
          [];

        setVisitHistory(Array.isArray(history) ? history : []);
      } catch (error) {
        if (!cancelled) {
          console.error("Visit history loading error:", error);
          setVisitHistory([]);
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    }, form.visitType === "party" ? 500 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, form.visitType, form.schoolId, form.partyName]);

  useEffect(() => {
    if (!open) return;

    const schoolId = form.schoolId;
    const partyName = form.partyName.trim();

    if (form.visitType === "school" && !schoolId) {
      setForm((prev) => ({ ...prev, visitNumber: "" }));
      return;
    }

    if (form.visitType === "party" && partyName.length < 2) {
      setForm((prev) => ({ ...prev, visitNumber: "" }));
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await getNextVisitNumber({
          visitType: form.visitType,
          schoolId: form.visitType === "school" ? schoolId : "",
          partyName: form.visitType === "party" ? partyName : "",
        });

        if (cancelled) return;

        const number =
          response?.data?.visitNumber ??
          response?.visitNumber ??
          "";

        setForm((prev) => ({
          ...prev,
          visitNumber: number ? String(number) : "",
        }));
      } catch (error) {
        if (!cancelled) {
          console.error("Visit number loading error:", error);
          setForm((prev) => ({ ...prev, visitNumber: "" }));
        }
      }
    }, form.visitType === "party" ? 450 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, form.visitType, form.schoolId, form.partyName]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadMasters = async () => {
      try {
        setSchoolLoading(true);
        setJourneyLoading(true);

        const [
          schoolResponse,
          attendanceResponse,
          journeyResponse,
        ] = await Promise.all([
          getSchools(1, 500, ""),
          getTodayAttendance(),
          getJourneyPlans(1, 100),
        ]);

        if (cancelled) return;

        const schoolList = extractSchools(schoolResponse);
        const attendance = extractAttendance(attendanceResponse);
        const plans = extractJourneyPlans(journeyResponse);

        setSchools(Array.isArray(schoolList) ? schoolList : []);
        setJourneyPlans(Array.isArray(plans) ? plans : []);

        setForm((prev) => ({
          ...prev,
          attendanceId: attendance?.id ? String(attendance.id) : "",
        }));
      } catch (error) {
        if (cancelled) return;

        console.error("Visit master loading error:", error);

        showError(
          error?.response?.data?.message ||
            "Unable to load visit master data."
        );
      } finally {
        if (!cancelled) {
          setSchoolLoading(false);
          setJourneyLoading(false);
        }
      }
    };

    setForm(
    visit
        ? {
              ...INITIAL_FORM,
              ...visit,

              visitNumber:
                  visit.visitNumber != null
                      ? String(visit.visitNumber)
                      : "",

              visitDate: String(
                  visit.visitDate || getToday()
              ).substring(0, 10),

              visitType: visit.visitType || "school",

              schoolId: String(
                  visit.schoolId ?? visit.school?.id ?? ""
              ),

              partyName:
                  visit.partyName || visit.party?.name || "",

              partyId: String(
                  visit.partyId ?? visit.party?.id ?? ""
              ),

              journeyPlanItemId: String(
                  visit.journeyPlanItemId ?? ""
              ),

              attendanceId: String(
                  visit.attendanceId ?? ""
              ),

              personMet: visit.personMet || "",
              designation: visit.designation || "",
              contactNumber: visit.contactNumber || "",
              purpose: visit.purpose || "",
              assessment: visit.assessment || "",
              followUp: visit.followUp || "",
              remarks: visit.remarks || "",
              latitude: visit.latitude ?? "",
              longitude: visit.longitude ?? "",
              address: visit.address || "",

              peopleMet: visit.peopleMet || [],
              photos: visit.photos || [],
              followups: visit.followups || [],
              samples: visit.samples || [],
              orders: visit.orders || [],
              payments: visit.payments || [],
          }
        : {
              ...INITIAL_FORM,
              visitDate: getToday(),
          });

    setVisitHistory([]);

    setSchools([]);
    setJourneyPlans([]);

    loadMasters();

    return () => {
      cancelled = true;
    };
  }, [open, visit]);

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const journeyPlanItems = useMemo(() => {
    if (!Array.isArray(journeyPlans)) return [];

    const selectedDate = form.visitDate;
    const items = [];

    journeyPlans.forEach((plan) => {
      let planDateMatches = true;

      if (plan?.planDate && selectedDate) {
        planDateMatches =
          String(plan.planDate).substring(0, 10) === selectedDate;
      }

      if (!planDateMatches) return;

      const planItems = Array.isArray(plan?.items) ? plan.items : [];

      planItems.forEach((item) => {
        if (!item?.id) return;

        const itemStatus = String(item.status || "").toLowerCase();

        if (["completed", "cancelled"].includes(itemStatus)) return;

        const itemType = String(item.itemType || "").toLowerCase();

        if (
          itemType &&
          form.visitType &&
          itemType !== form.visitType
        ) {
          return;
        }

        const customerName =
          item?.school?.name ||
          item?.party?.name ||
          item?.schoolName ||
          item?.partyName ||
          itemType ||
          "Visit";

        const sequence =
          item?.sequence !== undefined && item?.sequence !== null
            ? `#${item.sequence} `
            : "";

        const plannedTime = item?.plannedTime
          ? ` • ${item.plannedTime}`
          : "";

        const purpose = item?.purpose ? ` • ${item.purpose}` : "";

        items.push({
          value: String(item.id),
          label:
            `${sequence}${plan?.title || "Journey Plan"} - ` +
            `${customerName}${plannedTime}${purpose}`,
          raw: item,
          plan,
        });
      });
    });

    return items;
  }, [journeyPlans, form.visitDate, form.visitType]);

  const handleVisitTypeChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      visitType: value,
      schoolId: "",
      partyName: "",
      partyId: "",
      journeyPlanItemId: "",
    }));
  };

  const handleVisitDateChange = (event) => {
    setForm((prev) => ({
      ...prev,
      visitDate: event.target.value,
      journeyPlanItemId: "",
    }));
  };

  const getLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: "", longitude: "" });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: String(position.coords.latitude),
            longitude: String(position.coords.longitude),
          });
        },
        () => {
          resolve({ latitude: "", longitude: "" });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        }
      );
    });

  const submit = async (event) => {
    event.preventDefault();

    if (loading) return;

    if (!form.visitDate) {
      showError("Please select visit date.");
      return;
    }

    if (form.visitType === "school" && !form.schoolId) {
      showError("Please select a school.");
      return;
    }

    if (form.visitType === "party" && !form.partyName.trim()) {
      showError("Please enter party name.");
      return;
    }

    if (!form.purpose.trim()) {
      showError("Purpose is required.");
      return;
    }

    try {
      setLoading(true);

      const location = await getLocation();

      const payload = {
        visitDate: form.visitDate,
        visitType: form.visitType,

        ...(form.visitType === "school"
          ? { schoolId: form.schoolId }
          : { partyName: form.partyName.trim() }),

        ...(form.journeyPlanItemId
          ? { journeyPlanItemId: form.journeyPlanItemId }
          : {}),

        ...(form.attendanceId
          ? { attendanceId: form.attendanceId }
          : {}),

        ...(form.personMet.trim()
          ? { personMet: form.personMet.trim() }
          : {}),

        ...(form.designation.trim()
          ? { designation: form.designation.trim() }
          : {}),

        ...(form.contactNumber.trim()
          ? { contactNumber: form.contactNumber.trim() }
          : {}),

        purpose: form.purpose.trim(),

        ...(form.assessment.trim()
          ? { assessment: form.assessment.trim() }
          : {}),

        ...(form.followUp.trim()
          ? { followUp: form.followUp.trim() }
          : {}),

        ...(form.remarks.trim()
          ? { remarks: form.remarks.trim() }
          : {}),

        ...(location.latitude || form.latitude
          ? { latitude: location.latitude || form.latitude }
          : {}),

        ...(location.longitude || form.longitude
          ? { longitude: location.longitude || form.longitude }
          : {}),

        ...(form.address.trim()
          ? { address: form.address.trim() }
          : {}),

        // Existing child collections are preserved.
        peopleMet: form.peopleMet,
        photos: form.photos,
        followups: form.followups,
        samples: form.samples,
        orders: form.orders,
        payments: form.payments,
      };

      console.log(
          isEditMode ? "UPDATE VISIT PAYLOAD:" : "CREATE VISIT PAYLOAD:",
          payload
      );

      if (isEditMode) {
          delete payload.peopleMet;
          delete payload.photos;
          delete payload.followups;
          delete payload.samples;
          delete payload.orders;
          delete payload.payments;
          await updateVisit(visit.id, payload);
          showSuccess("Visit updated successfully.");
      } else {
          await createVisit(payload);
          showSuccess("Visit created successfully.");
      }

      if (typeof reload === "function") {
          await reload();
      }

      onClose();

      if (typeof reload === "function") {
        await reload();
      }

      onClose();
    } catch (error) {
      console.error("Create Visit Error:", error);

      showError(
        error?.response?.data?.message ||
          "Unable to create visit."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <CrudModal
      open={open}
      onClose={onClose}
      onSubmit={submit}
      loading={loading}
      title="Create Visit"
      submitText="Save Visit"
      width="max-w-6xl"
    >
      <CrudInput
        label="Visit Number"
        value={form.visitNumber || "Generated automatically"}
        readOnly
      />

      <CrudInput
        label="Visit Date"
        type="date"
        value={form.visitDate}
        onChange={handleVisitDateChange}
      />

      <CrudSelect
        label="Visit Type"
        value={form.visitType}
        onChange={handleVisitTypeChange}
        options={[
          { value: "school", label: "School" },
          { value: "party", label: "Party" },
        ]}
      />

      {form.visitType === "school" && (
        <SearchableSchoolSelect
          label="School Name"
          value={form.schoolId}
          schools={schools}
          loading={schoolLoading}
          onChange={(schoolId) => updateField("schoolId", schoolId)}
        />
      )}

      {/* {form.visitType === "school" && (
        <CrudSelect
          label="School Name"
          value={form.schoolId}
          onChange={(event) =>
            updateField("schoolId", event.target.value)
          }
          options={[
            {
              value: "",
              label: schoolLoading
                ? "Loading Schools..."
                : "Select School",
            },
            ...schools.map((school) => ({
              value: String(school.id),
              label:
                school.name ||
                school.schoolName ||
                `School #${school.id}`,
            })),
          ]}
        />
      )} */}

      {form.visitType === "party" && (
        <CrudInput
          label="Party Name"
          value={form.partyName}
          onChange={(event) =>
            updateField("partyName", event.target.value)
          }
          placeholder="Enter Party Name"
        />
      )}

      {(historyLoading || visitHistory.length > 0) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 md:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-800">
              Previous Visit History
            </div>
            <div className="text-xs text-gray-500">
              {historyLoading
                ? "Loading..."
                : `${visitHistory.length} previous visit${visitHistory.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {!historyLoading && visitHistory.length > 0 && (
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {visitHistory
                .slice()
                .reverse()
                .slice(0, 5)
                .map((item) => (
                  <div
                    key={item.visitId}
                    className="rounded-md border border-gray-200 bg-white p-2 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-2 font-medium text-gray-800">
                      <span>Visit #{item.visitNumber}</span>
                      <span>•</span>
                      <span>{String(item.visitDate || "").substring(0, 10)}</span>
                      {item.status && <span className="text-gray-500">• {item.status}</span>}
                    </div>
                    <div className="mt-1 text-gray-600">
                      {item.personMet || "No person recorded"}
                      {item.designation ? ` • ${item.designation}` : ""}
                      {item.contactNumber ? ` • ${item.contactNumber}` : ""}
                    </div>
                    {item.purpose && (
                      <div className="mt-1 text-gray-600">
                        <span className="font-medium">Purpose:</span> {item.purpose}
                      </div>
                    )}
                    {item.followUp && (
                      <div className="mt-1 text-gray-600">
                        <span className="font-medium">Follow-up:</span> {item.followUp}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      <CrudSelect
        label="Journey Plan Item"
        value={form.journeyPlanItemId}
        onChange={(event) =>
          updateField("journeyPlanItemId", event.target.value)
        }
        options={[
          {
            value: "",
            label: journeyLoading
              ? "Loading Journey Plans..."
              : journeyPlanItems.length
              ? "Select Planned Visit (Optional)"
              : "No Planned Visit Available",
          },
          ...journeyPlanItems.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
      />

      <CrudInput
        label="Person Met / Contact Person"
        value={form.personMet}
        onChange={(event) =>
          updateField("personMet", event.target.value)
        }
        placeholder="Enter person met / contact person"
      />

      <CrudInput
        label="Designation"
        value={form.designation}
        onChange={(event) =>
          updateField("designation", event.target.value)
        }
        placeholder="Enter designation"
      />

      <CrudInput
        label="Contact Number"
        type="tel"
        value={form.contactNumber}
        onChange={(event) =>
          updateField("contactNumber", event.target.value
          )
        }
        placeholder="Enter contact number"
      />

      <CrudSelect
  label="Purpose of Meeting"
  value={form.purpose}
  onChange={(event) =>
    updateField("purpose", event.target.value)
  }
  options={[
    { value: "", label: "Select Purpose of Meeting" },
    { value: "Introduction Meet", label: "Introduction Meet" },
    { value: "Courtesy", label: "Courtesy" },
    { value: "Sample", label: "Sample" },
    { value: "Follow Up", label: "Follow Up" },
    { value: "Payment", label: "Payment" },
  ]}
/>

      <CrudInput
        label="Assessment / Outcome of Visit"
        value={form.assessment}
        onChange={(event) =>
          updateField("assessment", event.target.value)
        }
        placeholder="Enter assessment / outcome"
      />

      <CrudInput
        label="Follow-up / Next Action"
        value={form.followUp}
        onChange={(event) =>
          updateField("followUp", event.target.value)
        }
        placeholder="Enter follow-up / next action"
      />

      <CrudInput
        label="Visit Remarks"
        value={form.remarks}
        onChange={(event) =>
          updateField("remarks", event.target.value)
        }
        placeholder="Enter visit remarks"
      />

      <CrudInput
        label="Address"
        value={form.address}
        onChange={(event) =>
          updateField("address", event.target.value)
        }
        placeholder="Optional address"
      />
    </CrudModal>
  );
}
